import * as Yup from 'yup'
import { PlanFormData, QuoteReviewProps, ServiceListing } from '../type'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { wrapStyle } from 'views/Support/SupportForm'
import { Box, Flex } from 'components/elements'
import { Listing, MenuDropdown } from 'components/menuDropdown'
import { useEffect, useMemo, useState } from 'react'
import FormWrapper from 'features/auth/components/FormWrapper'
import { Button } from 'components/button/Button'
import { useLocation, useNavigate } from 'react-router-dom'
import { AffiliateID, routes } from 'configs/constants'
import { ErrorBox } from 'features/profile/components/EditProfileForm'
import { Stages, ValidationErrors, TERMINOLOGY } from 'utils/enum'
import { ErrorCSS, Form, userRegHeadingCss } from 'views/style'
import { QouteLayout } from 'features/quote/page/GetAQuotes'
import { buttonCss } from './CoveragePlan'
import { useGetMerchantAffiliateDetails } from 'features/quote/api/getmerchantAffiliateDetails'

export const planScehema = Yup.object({
  planId: Yup.number().required(ValidationErrors.QuotePolicy),
})

//prod dlt affiliateId
const CARRIER_ID = 'fed8f54c31f368bf'

const PlanSelection = (props: QuoteReviewProps) => {
  const { quoteUserReg, setStage, setQuoteUserReg } = props
  const [insurancePlan, setInsurancePlan] = useState<Listing>()
  const [merchantAffiliateId, setIsMerchantAffiliateId] = useState('')

  const navigate = useNavigate()

  const location = useLocation()
  // //if url has sepecific affiliateId then user is coming from box carrier
  const searchParams = new URLSearchParams(location.search)
  const carrier_AffiliateId = searchParams.get('affiliateId')

  const { data: planList, isFetching: isPlanListingLoading, isError } = useGetMerchantAffiliateDetails({ affiliateId: merchantAffiliateId })

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm<PlanFormData>({
    resolver: yupResolver(planScehema),
    defaultValues: {
      planId: (quoteUserReg && quoteUserReg.planId) || undefined,
    },
  })

  //all the policy into select lisiting format
  const insurancePlansList = useMemo(() => {
    return planList?.data.insurancePlans || []
  }, [planList?.data.insurancePlans])

  const policyPlansList = useMemo(() => {
    if (insurancePlansList) {
      return insurancePlansList
        .filter((item: { id: number }) => {
          // Skip plan id 35 only if merchantAffiliateId is 'fed8f54c31f368bf'
          if (merchantAffiliateId === CARRIER_ID && item.id === 35) {
            return false
          }
          return true
        })
        .map(
          (
            items: { name: string; id: number; basePremium: string; maxCoverage: string; primaryService: ServiceListing; services: ServiceListing[]; discount: number },
            index: number
          ) => {
            return {
              label: items?.name,
              value: items?.id,
              key: index,
              discount: items?.discount,
              premiumAmount: items?.basePremium,
              maxCoverage: items?.maxCoverage,
              primaryService: items?.primaryService,
              services: items?.services,
            }
          }
        )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insurancePlansList])

  //if url has sepecific affiliateId then user is coming from box carrier
  //and then sepecific plan
  useEffect(() => {
    if (carrier_AffiliateId) setIsMerchantAffiliateId(carrier_AffiliateId)
  }, [carrier_AffiliateId])
  //if there is error then set the default value of affiliateId & go with that.
  useEffect(() => {
    if (isError) setIsMerchantAffiliateId(AffiliateID)
  }, [isError])
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])
  useEffect(() => {
    const plan = quoteUserReg && quoteUserReg?.planId

    if (policyPlansList) {
      const planDetails = policyPlansList.find((policy: { value: number }) => policy.value === plan)

      // Set the plan state with the matching label and value i already record exist
      if (planDetails) {
        setInsurancePlan({
          label: planDetails.label,
          value: planDetails.value,
          premiumAmount: planDetails.premiumAmount,
          maxCoverage: planDetails.maxCoverage,
          primaryService: planDetails.primaryService,
          services: planDetails.services,
          discount: planDetails?.discount,
        })
      }
    }
  }, [policyPlansList, quoteUserReg])
  const onSubmit = async (data: PlanFormData) => {
    const primaryService = insurancePlan?.primaryService
    //secondary service should not have the primary service
    const secondaryservices = insurancePlan?.services?.filter((service: { id: number }) => service.id !== primaryService?.id)

    const obj = {
      ...quoteUserReg,
      planId: data.planId,
      primaryService: primaryService,
      secondaryservices: secondaryservices,
      discount: insurancePlan?.discount,
      merchantAffiliateId,
    }

    setQuoteUserReg && setQuoteUserReg(obj)
    setStage && setStage(Stages.STAGE2)
  }

  return (
    <>
      <Flex align="start" justify="center" css={QouteLayout}>
        <FormWrapper controlWidth={{ maxWidth: 800 }}>
          <Flex align="center">
            <Flex as="h2" justify={'center'} css={userRegHeadingCss}>
              Get Quote
            </Flex>
          </Flex>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Box css={{ textAlign: 'left' }}>
              <Box css={wrapStyle} style={{ width: '100%', paddingLeft: 0 }}>
                <Controller
                  name={'planId'}
                  control={control}
                  render={({ field }) => (
                    <MenuDropdown
                      {...field}
                      isDisabled={isPlanListingLoading}
                      isResponseFetching={isPlanListingLoading}
                      {...register('planId')}
                      label={`Select ${TERMINOLOGY.warranty.singular}`}
                      placeholder={`Select ${TERMINOLOGY.warranty.singular}`}
                      value={insurancePlan}
                      list={policyPlansList}
                      setValue={e => {
                        setInsurancePlan(e)
                        field.onChange(e.value)
                        setValue('planId', e.value)
                      }}
                    />
                  )}
                />

                {!!errors.planId && <ErrorBox css={{ ...ErrorCSS, textAlign: 'left', mt: '$3' }}>{errors.planId?.message}</ErrorBox>}
              </Box>
              <Box as="h5" css={{ color: '$primary', textAlign: 'left', m: 0 }}></Box>
            </Box>

            <Box css={{ ...wrapStyle, textAlign: 'left', padding: 0 }} className="fullWidth">
              {!props?.quoteUserReg?.status && (
                <Box as="p">
                  Restore your previous flow?&nbsp;
                  <Box as="span" css={{ ...TermsCSS, cursor: 'pointer' }} onClick={() => props?.setStage && props?.setStage(Stages.STAGE6)}>
                    Click here
                  </Box>
                </Box>
              )}
            </Box>
            <Box css={buttonCss}>
              <Button bordered={true} type="button" onClick={() => navigate(routes.login)}>
                Cancel
              </Button>
              <Button type="submit">Next</Button>
            </Box>
          </Form>
        </FormWrapper>
      </Flex>
    </>
  )
}

export const TermsCSS = {
  color: '$primary',
  border: 'none',
  bgColor: 'transparent',
  textAlign: 'center',
  padding: '0px',
}

export const BorderCss = {
  border: '1px solid $white5',
}
export default PlanSelection
